/**
 * Google Analytics 4 (GA4) client for the Mowilex Customer Portal.
 *
 * Guardrails baked in:
 *   - Only initialises when REACT_APP_GOOGLE_ANALYTICS_ID is set AND
 *     REACT_APP_ENVIRONMENT === 'production'. Local dev + staging QA
 *     never send events -- keeps the prod dashboard clean.
 *   - Every helper is a no-op if initialisation was skipped. Callers
 *     don't need to check "is GA on?" -- just call and forget.
 *   - Backoffice pages should NOT call these helpers -- analytics is
 *     for customer-facing behaviour only. Enforced by convention, not
 *     by code.
 */

import ReactGA from 'react-ga4';

const MEASUREMENT_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_ID || '';
const ENV = process.env.REACT_APP_ENVIRONMENT || '';

let initialised = false;

/**
 * Initialise GA once at app boot. Safe to call multiple times -- only
 * fires on the first invocation. Silently no-ops if the env vars
 * aren't set to production values.
 */
export function initAnalytics(): void {
  if (initialised) return;
  if (!MEASUREMENT_ID) {
    // Explicitly no ID configured -- normal for dev + local. Don't
    // log noise on those envs.
    return;
  }
  if (ENV !== 'production') {
    // Prevent staging QA from polluting the prod GA property. If
    // Mowilex wants staging analytics later, spin up a second GA
    // property and gate that on ENV === 'staging' with a different
    // measurement ID env var.
    return;
  }

  ReactGA.initialize(MEASUREMENT_ID, {
    // gaOptions & gtagOptions left at defaults -- react-ga4 handles
    // the standard gtag.js payload correctly out of the box.
  });
  initialised = true;
}

/**
 * Record a page view. Called by <RouteTracker> on every react-router
 * location change (see components/analytics/RouteTracker.tsx).
 *
 * `pagePath` should be the URL path + search (e.g. "/products?category=X").
 * Pass window.location.pathname + window.location.search when in doubt.
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!initialised) return;
  ReactGA.send({
    hitType: 'pageview',
    page: pagePath,
    title: pageTitle || document.title,
  });
}

/**
 * Record a custom event. Keeps the payload flat and typed at the call
 * site so downstream analysts can see consistent property names in GA.
 *
 * Event naming convention: snake_case, past tense verb. Keep names
 * stable -- renaming an event orphans historical data in GA. See the
 * inventory in the commit that ships event instrumentation.
 */
/**
 * GA4 e-commerce item shape. Used inside the `items` array on
 * standard events like add_to_cart / begin_checkout / purchase --
 * GA4's Monetization report + Explorations only surface product-
 * level details when this shape matches.
 * See: https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */
export interface AnalyticsItem {
  item_id: string;          // internal / SKU-like identifier
  item_name?: string;       // human-readable product name
  price?: number;           // per-unit price
  quantity?: number;
  currency?: string;        // ISO-4217 (e.g. 'IDR')
}

export interface EventParams {
  // GA4 recommended properties (used across many events).
  value?: number;
  currency?: string;
  transaction_id?: string;  // required for `purchase` event in GA4
  items?: AnalyticsItem[];  // GA4 e-commerce array
  // Domain-specific properties -- kept optional + open. Anything not
  // listed here can still be passed via [key: string] below.
  order_id?: string;
  order_number?: string;
  invoice_id?: string;
  product_id?: string;
  product_code?: string;
  item_count?: number;
  quantity?: number;
  unit_price?: number;
  doc_type?: 'signed-invoice' | 'delivery-note' | 'faktur-pajak';
  role?: string;
  reason?: string;
  from?: string;
  to?: string;
  [key: string]: string | number | boolean | AnalyticsItem[] | undefined;
}

export function trackEvent(name: string, params?: EventParams): void {
  if (!initialised) return;
  // react-ga4's event() takes any object; TypeScript needs a cast
  // because the items[] payload doesn't match the narrow default type.
  // gtag.js accepts arrays fine -- this is a typing quirk, not a
  // runtime constraint.
  ReactGA.event(name, params as unknown as Record<string, string | number | boolean>);
}

/**
 * Set the current user's ID for cross-session analytics. Must be a
 * stable, non-PII identifier (portal UUID is fine; email is NOT).
 * Call once after login; call with null on logout.
 */
export function setUserId(userId: string | null): void {
  if (!initialised) return;
  ReactGA.set({ user_id: userId || undefined });
}
